import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card'
import { Button } from '../../ui/button'
import { Progress } from '../../ui/progress'
import {
  CheckCircle2,
  Circle,
  MinusCircle,
  ChevronRight,
  Wand2,
  X,
  ListChecks,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  EventSetupStatus,
  setupStepHref,
  wizardHref,
} from './eventSetupStatus'
import { SETUP_STEP_ICONS } from './setupStepIcons'

const storageKey = (eventId: number | string) =>
  `ems.eventSetup.checklist.dismissed.${eventId}.v1`

export const SHOW_SETUP_CHECKLIST_EVENT = 'ems:show-setup-checklist'

/** Re-opens a dismissed checklist. Mirrors the welcome banner's trigger. */
export const showSetupChecklist = (eventId: number | string) => {
  try {
    localStorage.removeItem(storageKey(eventId))
  } catch {
    /* private mode / quota — the event below still un-hides it */
  }
  window.dispatchEvent(new Event(SHOW_SETUP_CHECKLIST_EVENT))
}

interface EventSetupChecklistProps {
  eventId: number | string
  status: EventSetupStatus
  /** Whether the viewer can actually complete the schedule-admin steps. */
  canEdit: boolean
  /** Can the viewer provision people (IDENTITY_MANAGER+)? Defaults to canEdit. */
  canProvision?: boolean
  /** Persist a dismissal in localStorage and show a close button. */
  dismissible?: boolean
  /** Hide the checklist entirely once every step is done. */
  hideWhenComplete?: boolean
  showWizardButton?: boolean
  className?: string
}

/**
 * Persistent "what's left before this event can be scheduled" card. Shares its
 * step definitions with the wizard and the prerequisite alerts, so there is one
 * description of the setup order in the app.
 */
export const EventSetupChecklist: React.FC<EventSetupChecklistProps> = ({
  eventId,
  status,
  canEdit,
  canProvision,
  dismissible = false,
  hideWhenComplete = false,
  showWizardButton = true,
  className,
}) => {
  const [dismissed, setDismissed] = useState<boolean>(dismissible)

  useEffect(() => {
    if (!dismissible) return
    try {
      setDismissed(localStorage.getItem(storageKey(eventId)) === 'true')
    } catch {
      setDismissed(false)
    }
    const onShow = () => setDismissed(false)
    window.addEventListener(SHOW_SETUP_CHECKLIST_EVENT, onShow)
    return () => window.removeEventListener(SHOW_SETUP_CHECKLIST_EVENT, onShow)
  }, [dismissible, eventId])

  if (dismissible && dismissed) return null
  if (hideWhenComplete && status.isComplete) return null

  const handleDismiss = () => {
    try {
      localStorage.setItem(storageKey(eventId), 'true')
    } catch {
      /* best effort */
    }
    setDismissed(true)
  }

  const mayComplete = (requiredAccess: string) =>
    requiredAccess === 'IDENTITY_MANAGER' ? (canProvision ?? canEdit) : canEdit

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ListChecks className="h-5 w-5" />
              Schedule setup
            </CardTitle>
            <CardDescription>
              {status.isComplete
                ? 'Everything is in place — this event is ready to schedule.'
                : 'Finish these steps before you build the schedule.'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {showWizardButton && canEdit && !status.isComplete && (
              <Button asChild size="sm">
                <Link to={wizardHref(eventId, status.firstIncompleteStepId ?? undefined)}>
                  <Wand2 className="mr-2 h-4 w-4" />
                  Setup Wizard
                </Link>
              </Button>
            )}
            {dismissible && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleDismiss}
                aria-label="Hide setup checklist"
                title="Hide"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Progress value={status.percent} className="flex-1" />
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {status.completedCount} of {status.requiredCount} done
          </span>
        </div>

        <ul className="divide-y rounded-md border">
          {status.steps.map((step) => {
            const Icon = SETUP_STEP_ICONS[step.id]
            const actionable = mayComplete(step.requiredAccess)
            const row = (
              <div className="flex items-center gap-3 px-3 py-2.5">
                {step.status === 'complete' ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                ) : step.status === 'unknown' ? (
                  <MinusCircle className="h-4 w-4 shrink-0 text-muted-foreground/50" />
                ) : (
                  <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      step.status === 'complete' && 'text-muted-foreground'
                    )}
                  >
                    {step.label}
                    {step.optional && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        recommended
                      </span>
                    )}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {actionable
                      ? step.blurb
                      : `Requires a ${step.requiredAccess === 'IDENTITY_MANAGER' ? 'identity manager' : 'schedule administrator'}`}
                  </p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {step.detail}
                </span>
                {actionable && (
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
              </div>
            )

            return (
              <li key={step.id}>
                {actionable ? (
                  <Link
                    to={setupStepHref(step.id, eventId)}
                    className="block hover:bg-muted/50"
                  >
                    {row}
                  </Link>
                ) : (
                  <div className="opacity-70">{row}</div>
                )}
              </li>
            )
          })}
        </ul>

        {status.steps.some((s) => s.warnings.length > 0) && (
          <ul className="space-y-1">
            {status.steps.flatMap((s) =>
              s.warnings.map((w) => (
                <li key={`${s.id}-${w}`} className="text-xs text-amber-700">
                  {w}
                </li>
              ))
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
